import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { defineStore } from 'pinia';
import CaseTypesAPI from 'dashboard/api/caseTypes';
import SupportCasesAPI from 'dashboard/api/supportCases';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import { OWNER_SCOPE, SORT_OPTION } from 'dashboard/helper/supportCaseMeta';

/**
 * Support cases (user-facing: Tickets).
 *
 * State shape
 *   records          every case this session has seen, normalised by numeric id
 *   listIds          the current page of the list, in the order the server sent
 *                    it — the server is the only filter and sort authority, so
 *                    nothing here re-filters or re-sorts a returned page
 *   meta             pagination totals that came with the list
 *   counts           queue totals from GET /counts; one vocabulary — totals,
 *                    never unread
 *   filters          the query the list is fetched with; `queryParams` is the
 *                    single translation of it into the REST contract
 *   activeCaseId     the case the workroom is showing
 *   commentsByCase / activitiesByCase / tasksByCase
 *                    workroom sub-collections, keyed by case id
 *
 * API contract this store assumes (T-09/T-10): collections answer
 * `{ payload, meta }`, single records answer the case object itself, and every
 * body is snake_case, camelised once here so components never see two casings.
 *
 * The case domain owns its own state: nothing in this file imports from
 * `store/modules/conversations`, and no case screen reads the chat list's store.
 */

export const DEFAULT_FILTERS = {
  caseTypeId: null,
  stageId: null,
  category: null,
  owner: OWNER_SCOPE.ALL,
  archived: false,
  q: '',
  sortBy: SORT_OPTION.LAST_ACTIVITY,
  page: 1,
};

const DEFAULT_META = { count: 0, currentPage: 1, totalPages: 0 };

/**
 * The board's two shapes.
 *
 * A ticket type's workflow is one rail of stages, so a board drawn from it can
 * only ever show that one type — every other type's tickets have no column to
 * land in and silently disappear from a queue the list shows in full. So the
 * board only groups by STAGE when the queue is already narrowed to a single
 * type. Otherwise it groups by CATEGORY: the four system categories every
 * stage of every type maps onto (`case_stages.category`), which is the one
 * vocabulary the types share and therefore the only grouping whose columns
 * cover exactly the rows the list does.
 */
export const BOARD_GROUP_BY = { STAGE: 'stage', CATEGORY: 'category' };

// One kanban column: its page of case ids plus its own pagination. Columns
// paginate independently — both `case_stage_id` and `category` are first-class
// list filters (api.md), so a column is simply the list narrowed to itself.
const emptyBoardColumn = () => ({
  ids: [],
  meta: { ...DEFAULT_META },
  isLoading: false,
});

// `custom_fields` is a map, not a record: its keys are the field definitions' own
// `key`, which the server constrains to /\A[a-z0-9_]+\z/ and which the fields panel
// looks values up by. Case-converting them is data corruption — `order_id` becomes
// `orderId` on the way in and the panel's lookup misses, so a multi-word field
// renders blank — so the map's keys ride through untouched in BOTH directions while
// the envelope around it converts as usual.
const RAW_MAP_KEY = 'custom_fields';

// Exported because the case row is not only read here: the conversation bridge
// answers with link rows that carry a whole case one level down, and a consumer
// that camelised those with the plain helper would corrupt the same map. One
// rule, two entry points, no second copy of the key name.
export const camelizeSupportCase = data =>
  camelcaseKeys(data, { deep: true, stopPaths: [RAW_MAP_KEY] });

// `{ id, role, created_at, support_case: { … custom_fields } }` — the map sits
// one level deeper, so the stop path does too.
export const camelizeSupportCaseLink = data =>
  camelcaseKeys(data, {
    deep: true,
    stopPaths: [`support_case.${RAW_MAP_KEY}`],
  });

const camelize = camelizeSupportCase;

// The same map, addressed by its camelised name because this is the outbound half.
const snakecase = data =>
  snakecaseKeys(data, {
    deep: true,
    shouldRecurse: key => key !== 'customFields' && key !== RAW_MAP_KEY,
  });

export const useSupportCasesStore = defineStore('supportCases', {
  state: () => ({
    records: {},
    listIds: [],
    // The account's case types, from `GET /case_types`. Workspace config, not
    // queue state, so it is fetched by the surfaces that offer a type and not
    // refreshed with the list.
    caseTypes: [],
    meta: { ...DEFAULT_META },
    counts: {},
    filters: { ...DEFAULT_FILTERS },
    // The board view's columns, keyed by stage id or by stage category
    // depending on `boardGroupBy`. Board and list are two projections of the
    // same records: only membership lives here.
    boardColumns: {},
    boardGroupBy: BOARD_GROUP_BY.STAGE,
    activeCaseId: null,
    commentsByCase: {},
    activitiesByCase: {},
    tasksByCase: {},
    uiFlags: {
      isFetchingList: false,
      isFetchingItem: false,
      isCreatingItem: false,
      isUpdatingItem: false,
      isFetchingThread: false,
    },
    fetchRequestToken: 0,
    boardRequestToken: 0,
  }),

  getters: {
    cases: state => state.listIds.map(id => state.records[id]).filter(Boolean),

    activeCase: state => state.records[state.activeCaseId] || null,

    // The router addresses a case by display_id; the store keys by id.
    caseByDisplayId: state => displayId =>
      Object.values(state.records).find(
        record => String(record.displayId) === String(displayId)
      ) || null,

    // The types this user may open a case in, and the options the type filter
    // offers. `GET /case_types` is the authority — it answers the permission
    // scope's set, name-ordered, and bootstraps an account that has none — so
    // the derivation from seen case rows is only the answer before that request
    // resolves (or on a server that predates the endpoint). A type is never
    // invented here: both sources are rows the server sent.
    caseTypeOptions: state => {
      if (state.caseTypes.length) return state.caseTypes;

      return Object.values(
        Object.values(state.records).reduce((types, record) => {
          const caseType = record?.caseType;
          if (caseType?.id) types[caseType.id] = caseType;
          return types;
        }, {})
      ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    commentsFor: state => caseId => state.commentsByCase[caseId] || [],

    activitiesFor: state => caseId => state.activitiesByCase[caseId] || [],

    tasksFor: state => caseId => state.tasksByCase[caseId] || [],

    // The one place filters become a request. Empty filters are omitted rather
    // than sent as nulls, so a shared list URL carries only what it narrows.
    queryParams: state => {
      const {
        caseTypeId,
        stageId,
        category,
        owner,
        archived,
        q,
        sortBy,
        page,
      } = state.filters;

      return {
        ...(caseTypeId ? { case_type_id: caseTypeId } : {}),
        ...(stageId ? { case_stage_id: stageId } : {}),
        ...(category ? { category } : {}),
        ...(owner && owner !== OWNER_SCOPE.ALL ? { owner } : {}),
        ...(archived ? { archived: true } : {}),
        ...(q ? { q } : {}),
        sort: sortBy,
        page,
      };
    },

    // What one board column renders: its cases in server order, plus its own
    // pagination. A column the board has not fetched answers empty rather than
    // undefined, so columns render before their first page lands.
    boardColumnFor: state => key => {
      const column = state.boardColumns[key] || emptyBoardColumn();
      return {
        cases: column.ids.map(id => state.records[id]).filter(Boolean),
        meta: column.meta,
        isLoading: column.isLoading,
      };
    },

    // The board's translation of the filters: the same query as the list minus
    // the dimensions the board itself is — the column is a stage or a category,
    // so either narrowing would empty every other column — and minus `page`,
    // which is per column.
    boardParams() {
      const params = { ...this.queryParams };
      delete params.case_stage_id;
      delete params.category;
      delete params.page;
      return params;
    },

    // The filter that selects one column, in the dimension the board groups by.
    boardColumnParams: state => key =>
      state.boardGroupBy === BOARD_GROUP_BY.CATEGORY
        ? { category: key }
        : { case_stage_id: key },

    // Which column a case belongs to under the board's current grouping.
    boardKeyForCase: state => record =>
      state.boardGroupBy === BOARD_GROUP_BY.CATEGORY
        ? record?.caseStage?.category
        : record?.caseStage?.id,

    // `GET /counts` reports the dimensions the list is NOT narrowed by, and it
    // is not a page of anything: it accepts type, archived and the search only
    // (api.md). Sending `page` or a facet it deliberately ignores would read as
    // a filter it honours.
    countsParams: state => {
      const { caseTypeId, archived, q } = state.filters;

      return {
        ...(caseTypeId ? { case_type_id: caseTypeId } : {}),
        ...(archived ? { archived: true } : {}),
        ...(q ? { q } : {}),
      };
    },
  },

  actions: {
    upsertCase(record) {
      if (!record?.id) return null;
      this.records[record.id] = { ...this.records[record.id], ...record };
      return this.records[record.id];
    },

    // Any filter change starts a new result set, so it returns to page 1 unless
    // the change *is* a page turn.
    setFilters(filters = {}) {
      const page = filters.page ?? 1;
      this.filters = { ...this.filters, ...filters, page };
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
    },

    setActiveCase(caseId) {
      this.activeCaseId = caseId;
    },

    async fetchCases() {
      this.uiFlags.isFetchingList = true;
      this.fetchRequestToken += 1;
      const requestToken = this.fetchRequestToken;

      try {
        const { data } = await SupportCasesAPI.get(this.queryParams);
        // A newer fetch (filter or page change) superseded this one.
        if (this.fetchRequestToken !== requestToken) return this.cases;

        const records = camelize(data.payload);
        records.forEach(record => this.upsertCase(record));
        this.listIds = records.map(record => record.id);
        this.meta = camelize(data.meta);
        return this.cases;
      } catch (error) {
        if (this.fetchRequestToken !== requestToken) return this.cases;
        // Drop the stale page so rows from the previous query are not shown
        // under the new one.
        this.listIds = [];
        this.meta = { ...DEFAULT_META };
        return throwErrorMessage(error);
      } finally {
        if (this.fetchRequestToken === requestToken) {
          this.uiFlags.isFetchingList = false;
        }
      }
    },

    async fetchCounts() {
      try {
        const { data } = await SupportCasesAPI.getCounts(this.countsParams);
        this.counts = camelize(data);
        return this.counts;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // One column's page. `page > 1` appends (the column's "load more"); page 1
    // replaces. Records land in the shared `records` map, so a card and a list
    // row can never disagree about the same case.
    async fetchBoardColumn(key, { page = 1 } = {}) {
      const column = this.boardColumns[key] || emptyBoardColumn();
      this.boardColumns[key] = column;
      const requestToken = this.boardRequestToken;
      column.isLoading = true;

      try {
        const { data } = await SupportCasesAPI.get({
          ...this.boardParams,
          ...this.boardColumnParams(key),
          page,
        });
        // A newer board fetch (filter or type change) superseded this one.
        if (this.boardRequestToken !== requestToken) return;

        const records = camelize(data.payload);
        records.forEach(record => this.upsertCase(record));
        const ids = records.map(record => record.id);
        column.ids =
          page > 1
            ? [...column.ids.filter(id => !ids.includes(id)), ...ids]
            : ids;
        column.meta = camelize(data.meta);
      } catch (error) {
        if (this.boardRequestToken !== requestToken) return;
        throwErrorMessage(error);
      } finally {
        if (this.boardRequestToken === requestToken) {
          column.isLoading = false;
        }
      }
    },

    // The whole board: one request per column, in parallel. Columns are seeded
    // synchronously so the rail of empty columns renders while the pages land.
    // The grouping is set before the seeding, because it decides both the key a
    // column is stored under and the filter it is fetched with.
    async fetchBoard(keys = [], { groupBy = BOARD_GROUP_BY.STAGE } = {}) {
      this.boardRequestToken += 1;
      this.boardGroupBy = groupBy;
      this.boardColumns = {};
      keys.forEach(key => {
        this.boardColumns[key] = emptyBoardColumn();
      });
      await Promise.all(keys.map(key => this.fetchBoardColumn(key)));
    },

    // The column a card was dropped on names a real stage when the board is one
    // type's workflow. When it spans every type the column is a category, which
    // has to be resolved against the card's OWN type before it can be written -
    // two types reach "In progress" through stages of their own. A type with no
    // active stage in that category has nowhere to put the card, and answers
    // null so the screen can say so instead of writing a stage from elsewhere.
    resolveStageForDrop(record, column) {
      if (column?.id) return column;

      const caseType = this.caseTypes.find(
        type => type.id === record?.caseType?.id
      );
      return (
        (caseType?.workflowStages || []).find(
          stage => stage.category === column?.category
        ) || null
      );
    },

    // The drag. Optimistic on both column membership and the record's stage,
    // with a snapshot rollback: a card that could not move must land back in
    // the column it left, not vanish. The moved card goes to the top of its
    // new column because the stage change touches `updated_at` and the board
    // sorts by last activity — the server would answer the same order.
    async moveCaseToStage(caseId, toStage) {
      const record = this.records[caseId];
      if (!record || !toStage?.id || record.caseStage?.id === toStage.id) {
        return record;
      }

      // Read before the optimistic write: the card's column is derived from the
      // stage it is still sitting in.
      const fromColumn = this.boardColumns[this.boardKeyForCase(record)];
      const toColumn =
        this.boardColumns[
          this.boardKeyForCase({
            caseStage: toStage,
          })
        ];
      const snapshot = {
        caseStage: record.caseStage,
        fromIds: fromColumn ? [...fromColumn.ids] : null,
        fromCount: fromColumn?.meta.count,
        toIds: toColumn ? [...toColumn.ids] : null,
        toCount: toColumn?.meta.count,
      };

      record.caseStage = { ...toStage };
      if (fromColumn) {
        fromColumn.ids = fromColumn.ids.filter(id => id !== record.id);
        fromColumn.meta.count = Math.max(0, fromColumn.meta.count - 1);
      }
      if (toColumn && !toColumn.ids.includes(record.id)) {
        toColumn.ids = [record.id, ...toColumn.ids];
        toColumn.meta.count += 1;
      }

      try {
        const { data } = await SupportCasesAPI.changeStage(
          record.displayId,
          toStage.id
        );
        return this.upsertCase(camelize(data));
      } catch (error) {
        record.caseStage = snapshot.caseStage;
        if (fromColumn) {
          fromColumn.ids = snapshot.fromIds;
          fromColumn.meta.count = snapshot.fromCount;
        }
        if (toColumn) {
          toColumn.ids = snapshot.toIds;
          toColumn.meta.count = snapshot.toCount;
        }
        return throwErrorMessage(error);
      }
    },

    // The detail read carries the thread and the checklist with it (api.md), so
    // the workroom seeds both sub-collections from that one response rather than
    // opening with a request per panel. The activity trail is the exception: it
    // is its own paginated endpoint.
    async fetchCase(displayId) {
      this.uiFlags.isFetchingItem = true;
      try {
        const { data } = await SupportCasesAPI.show(displayId);
        const record = this.upsertCase(camelize(data));
        this.activeCaseId = record.id;
        this.commentsByCase[record.id] = record.comments || [];
        this.tasksByCase[record.id] = record.tasks || [];
        return record;
      } catch (error) {
        // The workroom answers a missing ticket with its own screen, and
        // `throwErrorMessage` reduces every failure to a message string, so the
        // 404 is re-raised intact for the screen to read.
        if (error?.response?.status === 404) throw error;
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isFetchingItem = false;
      }
    },

    // The type catalogue every picker reads. Fetched once per surface that
    // needs it rather than kept fresh: types are workspace configuration, not
    // queue state, and T-13's editor is the thing that will invalidate them.
    async fetchCaseTypes({ force = false } = {}) {
      if (this.caseTypes.length && !force) return this.caseTypes;

      try {
        const { data } = await CaseTypesAPI.get();
        this.caseTypes = camelize(data.payload);
      } catch (error) {
        // The pickers fall back to the types seen on case rows, and the create
        // dialog states its empty case. Nothing on screen fails over this.
      }
      return this.caseTypes;
    },

    // Every settings write answers with the whole type, rail included, so the
    // editor never has to refetch to stay in step. An archived type drops out
    // of the catalogue, which is what "no longer offered" means everywhere else
    // this list is read.
    upsertCaseType(caseType) {
      const next = this.caseTypes.filter(type => type.id !== caseType.id);
      if (!caseType.archived) next.push(caseType);
      this.caseTypes = next.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
      return caseType;
    },

    async createCaseType(payload) {
      this.uiFlags.isCreatingItem = true;
      try {
        const { data } = await CaseTypesAPI.create(snakecase(payload));
        return this.upsertCaseType(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isCreatingItem = false;
      }
    },

    async updateCaseType(id, payload) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await CaseTypesAPI.update(id, snakecase(payload));
        return this.upsertCaseType(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    // Archival, not deletion: the type stops being offered while every ticket
    // already filed under it stays readable.
    async archiveCaseType(id) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await CaseTypesAPI.delete(id);
        return this.upsertCaseType(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    async addStage(caseTypeId, payload) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.createStage(caseTypeId, snakecase(payload))
      );
    },

    async updateStage(caseTypeId, stageId, payload) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.updateStage(caseTypeId, stageId, snakecase(payload))
      );
    },

    async archiveStage(caseTypeId, stageId, options = {}) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.archiveStage(caseTypeId, stageId, options)
      );
    },

    async reorderStages(caseTypeId, stageIds) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.reorderStages(caseTypeId, stageIds)
      );
    },

    // The custom fields a ticket of this type is made of. Same four writes as
    // the rail, against the same type payload — `key` is only read on create.
    async addFieldDefinition(caseTypeId, payload) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.createFieldDefinition(caseTypeId, snakecase(payload))
      );
    },

    async updateFieldDefinition(caseTypeId, definitionId, payload) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.updateFieldDefinition(
          caseTypeId,
          definitionId,
          snakecase(payload)
        )
      );
    },

    async archiveFieldDefinition(caseTypeId, definitionId) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.archiveFieldDefinition(caseTypeId, definitionId)
      );
    },

    async reorderFieldDefinitions(caseTypeId, definitionIds) {
      return this.writeCaseTypeConfig(() =>
        CaseTypesAPI.reorderFieldDefinitions(caseTypeId, definitionIds)
      );
    },

    // Every settings write - rail or field - differs only in the request it
    // sends: each answers with the whole type, and each surfaces the server's
    // guard-rail refusal verbatim.
    async writeCaseTypeConfig(request) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await request();
        return this.upsertCaseType(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    async createCase(payload) {
      this.uiFlags.isCreatingItem = true;
      try {
        const { data } = await SupportCasesAPI.create(snakecase(payload));
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isCreatingItem = false;
      }
    },

    async updateCase(displayId, payload) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await SupportCasesAPI.update(
          displayId,
          snakecase(payload)
        );
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    async changeStage(displayId, stageId) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await SupportCasesAPI.changeStage(displayId, stageId);
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    async assignCase(displayId, owner = {}) {
      this.uiFlags.isUpdatingItem = true;
      try {
        const { data } = await SupportCasesAPI.assign(displayId, owner);
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isUpdatingItem = false;
      }
    },

    async archiveCase(displayId) {
      try {
        const { data } = await SupportCasesAPI.archive(displayId);
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async unarchiveCase(displayId) {
      try {
        const { data } = await SupportCasesAPI.unarchive(displayId);
        return this.upsertCase(camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async fetchComments(displayId, params = {}) {
      this.uiFlags.isFetchingThread = true;
      try {
        const { data } = await SupportCasesAPI.getComments(displayId, params);
        const comments = camelize(data.payload);
        const record = this.caseByDisplayId(displayId);
        if (record) this.commentsByCase[record.id] = comments;
        return comments;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isFetchingThread = false;
      }
    },

    async addComment(displayId, { content, parentId = null }) {
      try {
        const { data } = await SupportCasesAPI.createComment(displayId, {
          content,
          parentId,
        });
        const comment = camelize(data);
        const record = this.caseByDisplayId(displayId);
        if (record) {
          this.commentsByCase[record.id] = [
            ...(this.commentsByCase[record.id] || []),
            comment,
          ];
        }
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // An edit and an archive both answer with the comment in its new shape — a
    // tombstone is still the comment — so one replacement covers both, and a
    // reply hanging off an archived parent keeps its anchor.
    replaceComment(displayId, comment) {
      const record = this.caseByDisplayId(displayId);
      if (!record) return comment;

      this.commentsByCase[record.id] = (
        this.commentsByCase[record.id] || []
      ).map(entry => (entry.id === comment.id ? comment : entry));
      return comment;
    },

    async editComment(displayId, commentId, content) {
      try {
        const { data } = await SupportCasesAPI.updateComment(
          displayId,
          commentId,
          { content }
        );
        return this.replaceComment(displayId, camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async archiveComment(displayId, commentId) {
      try {
        const { data } = await SupportCasesAPI.deleteComment(
          displayId,
          commentId
        );
        return this.replaceComment(displayId, camelize(data));
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async addTask(displayId, { title }) {
      try {
        const { data } = await SupportCasesAPI.createTask(displayId, { title });
        const task = camelize(data);
        const record = this.caseByDisplayId(displayId);
        if (record) {
          this.tasksByCase[record.id] = [
            ...(this.tasksByCase[record.id] || []),
            task,
          ];
        }
        return task;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async toggleTask(displayId, taskId, completed) {
      try {
        const { data } = await SupportCasesAPI.updateTask(displayId, taskId, {
          completed,
        });
        const task = camelize(data);
        const record = this.caseByDisplayId(displayId);
        if (record) {
          this.tasksByCase[record.id] = (this.tasksByCase[record.id] || []).map(
            entry => (entry.id === task.id ? task : entry)
          );
        }
        return task;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // Archiving drops the item from the checklist: the endpoint only ever
    // returns active tasks, so keeping it would resurrect on the next fetch.
    async archiveTask(displayId, taskId) {
      try {
        const { data } = await SupportCasesAPI.deleteTask(displayId, taskId);
        const record = this.caseByDisplayId(displayId);
        if (record) {
          this.tasksByCase[record.id] = (
            this.tasksByCase[record.id] || []
          ).filter(entry => entry.id !== taskId);
        }
        return camelize(data);
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // The unlink answers with the case row, which carries no links, so the row
    // the workroom renders keeps the links it knew about minus this one.
    async unlinkConversation(displayId, linkId) {
      try {
        const { data } = await SupportCasesAPI.deleteConversationLink(
          displayId,
          linkId
        );
        const record = this.upsertCase(camelize(data));
        if (record) {
          record.conversationLinks = (record.conversationLinks || []).filter(
            link => link.id !== linkId
          );
        }
        return record;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async fetchActivities(displayId, params = {}) {
      try {
        const { data } = await SupportCasesAPI.getActivities(displayId, params);
        const activities = camelize(data.payload);
        const record = this.caseByDisplayId(displayId);
        if (record) this.activitiesByCase[record.id] = activities;
        return activities;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async fetchTasks(displayId) {
      try {
        const { data } = await SupportCasesAPI.getTasks(displayId);
        const tasks = camelize(data.payload);
        const record = this.caseByDisplayId(displayId);
        if (record) this.tasksByCase[record.id] = tasks;
        return tasks;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },
  },
});
